import type { BrandAlertCreativeData, BrandAlertsPlayer, ShowTrigger } from './types';

const PLAY_POLL_INTERVAL_MS = 1000;

/**
 * Expand `expand_time_seconds` after playback starts.
 *
 * Both platforms share the schedule and differ only in how they learn that
 * playback began, so that is the single abstract method. The poll loop is
 * inherited from the JavaScript original: the trigger starts before the creative
 * is fetched, so it waits for whichever of the two arrives second.
 */
abstract class PlaybackTrigger implements ShowTrigger {
    protected _playStartedTime: number | null = null;
    private _delaySeconds = 0;
    private _callback: (() => void) | null = null;
    private _armCallback: (() => void) | null = null;
    private _timer: ReturnType<typeof setTimeout> | null = null;
    private _destroyed = false;
    private _ready = false;

    onReady(callback: () => void): void {
        this._callback = callback;
    }

    isReady(): boolean {
        return this._ready && !this._destroyed;
    }

    onArm(callback: () => void): void {
        this._armCallback = callback;
    }

    start(): void {
        this._watchPlaybackStart();
        // The video player page has always fetched at page load, independently of
        // playback — an alert can be served and then wait for the video to start.
        this._armCallback?.();
    }

    scheduleShow(creative: BrandAlertCreativeData): void {
        this._delaySeconds = creative.expand_time_seconds;
        this._checkPlayStarted();
    }

    destroy(): void {
        this._destroyed = true;
        if (this._timer) clearTimeout(this._timer);
        this._timer = null;
        this._callback = null;
        this._armCallback = null;
    }

    /** Set `_playStartedTime` when the platform reports playback starting. */
    protected abstract _watchPlaybackStart(): void;

    private _checkPlayStarted(): void {
        if (this._destroyed) return;

        if (!this._playStartedTime) {
            this._timer = setTimeout(() => this._checkPlayStarted(), PLAY_POLL_INTERVAL_MS);
            return;
        }

        const elapsed = Date.now() - this._playStartedTime;
        const remaining = Math.max(this._delaySeconds * 1000 - elapsed, 0);
        this._timer = setTimeout(() => {
            this._ready = true;
            this._callback?.();
        }, remaining);
    }
}

/**
 * Web: the Brightcove player's own events. The only place that knows about it,
 * so a surface with no player needs no stub.
 */
export class PlayerPlaybackTrigger extends PlaybackTrigger {
    constructor(private readonly _player: BrandAlertsPlayer) {
        super();
    }

    protected override _watchPlaybackStart(): void {
        // The player wrapper exposes no `off`, so these cannot be torn down — same
        // constraint `WebSignals` carries. Harmless: they only assign a field, and
        // the schedule they feed is cleared on destroy.
        const setPlayStartedTime = () => {
            if (this._playStartedTime) return;
            if (this._player.ended()) return;
            // Backdate the start time based on the current playhead, if available.
            this._playStartedTime = Date.now() - this._player.currentTime() * 1000;
        };

        if (!this._player.paused() && !this._player.ended()) {
            setPlayStartedTime();
        }

        this._player.one('play', setPlayStartedTime);
        this._player.one('contentresumed', setPlayStartedTime);
    }
}

/**
 * iOS: the native app posts `ios_playback_started` into the webview.
 */
export class NativePlaybackTrigger extends PlaybackTrigger {
    private _teardown: (() => void) | null = null;

    protected override _watchPlaybackStart(): void {
        const handler = () => {
            this._playStartedTime = Date.now();
        };
        window.addEventListener('ios_playback_started', handler);
        this._teardown = () => window.removeEventListener('ios_playback_started', handler);
    }

    override destroy(): void {
        this._teardown?.();
        this._teardown = null;
        super.destroy();
    }
}

/**
 * One HCP interaction with the Video Assistant.
 */
export interface ChatActivity {
    /**
     * The `Viewed` row of the assistant's current chat session, which the BVA
     * SideGuide is served against instead of the video page's uuid.
     *
     * Null until the session exists: it is minted by `POST /api/chats/`, which the
     * assistant only calls on the HCP's first submitted question. A conversation
     * reset mints a new one, so it is re-read per session, never cached for the
     * page's lifetime.
     */
    chatViewedUuid: string | null;
    /** The HCP submitted a question, as opposed to hovering, typing or scrolling. */
    isPrompt: boolean;
}

/**
 * The Video Assistant's signals: what the HCP does, and when the assistant is
 * busy answering. One arms the SideGuide, the other decides when its countdown may run.
 */
export interface ActivitySource {
    subscribe(onActivity: (activity: ChatActivity) => void): VoidFunction;
    subscribeStreaming(onStreaming: (streaming: boolean) => void): VoidFunction;
}

/**
 * The Video Assistant surface: expand after `bva_expand_time_seconds` of
 * inactivity, once the HCP has asked the assistant something.
 *
 * Idle until the first *submitted prompt that has a chat session behind it*.
 * Armed after it, with every further interaction of any kind resetting the countdown.
 *
 * Readiness can be revoked while display is postponed. The client destroys this
 * trigger once shown or permanently blocked, consuming the display for this video view.
 */
export class ChatbotInactivityTrigger implements ShowTrigger {
    private _delaySeconds: number | null = null;
    private _callback: (() => void) | null = null;
    private _armCallback: (() => void) | null = null;
    private _timer: ReturnType<typeof setTimeout> | null = null;
    private _unsubscribe: (() => void) | null = null;
    private _unsubscribeStreaming: (() => void) | null = null;
    private _streaming = false;
    private _armed = false;
    private _ready = false;
    private _destroyed = false;
    private _chatViewedUuid: string | null = null;

    constructor(private readonly _source: ActivitySource) {}

    get chatViewedUuid(): string | null {
        return this._chatViewedUuid;
    }

    onReady(callback: () => void): void {
        this._callback = callback;
    }

    isReady(): boolean {
        return this._ready && !this._destroyed;
    }

    onArm(callback: () => void): void {
        this._armCallback = callback;
    }

    start(): void {
        this._unsubscribe = this._source.subscribe((activity) => this._onActivity(activity));
        this._unsubscribeStreaming = this._source.subscribeStreaming((streaming) => this._onStreaming(streaming));
    }

    scheduleShow(creative: BrandAlertCreativeData): void {
        this._delaySeconds = creative.bva_expand_time_seconds;
        // The creative arrives after the arming interaction, so start counting
        // from now rather than from an interaction that has already passed.
        this._restartCountdown();
    }

    destroy(): void {
        this._destroyed = true;
        this._unsubscribe?.();
        this._unsubscribe = null;
        this._unsubscribeStreaming?.();
        this._unsubscribeStreaming = null;
        this._clear();
        this._callback = null;
        this._armCallback = null;
    }

    private _onActivity({ chatViewedUuid, isPrompt }: ChatActivity): void {
        if (this._destroyed) {
            return;
        }

        if (chatViewedUuid) {
            this._chatViewedUuid = chatViewedUuid;
        }

        if (!this._armed) {
            // Only a submitted prompt arms, and only once there is a chat session
            // to serve against. Browsing the assistant without asking it anything
            // must not spend a creative, and a fetch before the session exists
            // 404s — the endpoint resolves the video through that Viewed row.
            if (!isPrompt || !this._chatViewedUuid) {
                return;
            }

            this._armed = true;
            // Fetch now: the payload carries the delay this countdown needs, so a
            // lazy fetch at display time could never know how long to wait.
            this._armCallback?.();
            return;
        }

        this._restartCountdown();
    }

    private _onStreaming(streaming: boolean): void {
        if (this._destroyed) {
            return;
        }

        this._streaming = streaming;
        // Starting: drop the pending countdown. Finishing: start a fresh full one,
        // so the delay is measured from the answer landing rather than from the
        // question. Restarting unconditionally on the way out also recovers if the
        // start was missed — the client subscribes only after the assistant renders.
        if (streaming) {
            this._clear();
        } else {
            this._restartCountdown();
        }
    }

    private _restartCountdown(): void {
        if (this._destroyed || this._delaySeconds === null) {
            return;
        }

        if (this._streaming) {
            this._clear();
            return;
        }

        this._clear();

        this._timer = setTimeout(() => {
            this._timer = null;
            this._ready = true;
            this._callback?.();
        }, this._delaySeconds * 1000);
    }

    private _clear(): void {
        this._ready = false;
        if (this._timer) {
            clearTimeout(this._timer);
        }

        this._timer = null;
    }
}

/**
 * Web: the assistant's own document events, so the trigger never reads its DOM.
 */
export class ChatbotActivitySource implements ActivitySource {
    subscribe(onActivity: (activity: ChatActivity) => void): VoidFunction {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            onActivity({
                chatViewedUuid: detail?.chatViewedUuid ?? null,
                isPrompt: detail?.isPrompt === true,
            });
        };
        document.addEventListener('vm-chatbot:activity', handler);
        return () => document.removeEventListener('vm-chatbot:activity', handler);
    }

    subscribeStreaming(onStreaming: (streaming: boolean) => void): VoidFunction {
        const handler = (e: Event) => onStreaming((e as CustomEvent).detail?.streaming === true);
        document.addEventListener('vm-chatbot:streaming', handler);
        return () => document.removeEventListener('vm-chatbot:streaming', handler);
    }
}
